import type { ComponentType } from 'react';
import { useApiHolder } from '@backstage/frontend-plugin-api';
import type {
  AccessTabProps,
  AppTypeModule,
  BranchReadiness,
  ConfigurationTabProps,
  KeptSummaryProps,
  EnvironmentVersions,
  PodTools,
  PodToolsContext,
  ReadinessContext,
} from './appTypeModule';
import type { PodRoleRule } from '../projectEnvironments/podNames';
import { LEGACY_APP_TYPE } from '../shared/projectIdentity';
import { GenericConfigurationTab } from '../projectEnvironments/configurationForm/GenericConfigurationTab';
import { CredentialsCard } from '../projectEnvironments/CredentialsCard';
import { appTypeModulesApiRef } from './appTypeModulesApi';

/** Une identité pour « aucun module », stable d'un rendu à l'autre. */
const NO_MODULES: readonly AppTypeModule[] = Object.freeze([]);

/**
 * Les modules de type d'application du registre ; aucun sans registre (une
 * application, ou un test, qui ne l'enregistre pas).
 */
export function useAppTypeModules(): readonly AppTypeModule[] {
  return useApiHolder().get(appTypeModulesApiRef)?.list() ?? NO_MODULES;
}

/** Le module du type `appType`, s'il y en a un. */
export function useAppTypeModule(
  appType: string | undefined,
): AppTypeModule | undefined {
  return moduleOf(useAppTypeModules(), appType);
}

/**
 * Le contenu de l'onglet « Configuration » d'une page du type `appType` :
 * celui de son module ; sinon — un module sans onglet, un type sans module,
 * un type inconnu — l'onglet générique du cœur (`GenericConfigurationTab`,
 * plan « généricité », étape 5, 2026-10-24), construit depuis le
 * descripteur que la route de configuration envoie : il convient à tout
 * type, Odoo compris. Un type PAS ENCORE connu (en cours de lecture) est à
 * l'appelant d'attendre : `BranchPage` montre un Skeleton tant que l'entrée
 * du catalogue n'est pas lue, plutôt que de monter l'onglet générique puis
 * de le remplacer (brouillon perdu).
 */
export function useConfigurationTab(
  appType: string | undefined,
): ComponentType<ConfigurationTabProps> {
  return (
    moduleOf(useAppTypeModules(), appType)?.branch?.ConfigurationTab ??
    GenericConfigurationTab
  );
}

/**
 * Le contenu de l'onglet « Accès » d'une page du type `appType` : celui de
 * son module ; sinon, pour un type dont les fonctions (`spec.features`,
 * `features`) comprennent `credentials`, la carte des identifiants du cœur
 * (`CredentialsCard`, plan « généricité », étape 7, 2026-10-24) ; rien sinon,
 * ni pour un type pas encore connu (aucune requête d'un type supposé).
 */
export function useAccessTab(
  appType: string | undefined,
  features: ReadonlySet<string> | undefined,
): ComponentType<AccessTabProps> | undefined {
  const contributed = moduleOf(useAppTypeModules(), appType)?.branch?.AccessTab;
  if (appType === undefined) return undefined;
  if (contributed) return contributed;
  return features?.has('credentials') ? CredentialsCard : undefined;
}

/** Le premier module du type `appType`, celui que `get` du registre donne. */
function moduleOf(
  modules: readonly AppTypeModule[],
  appType: string | undefined,
): AppTypeModule | undefined {
  return appType === undefined
    ? undefined
    : modules.find(module => module.appType === appType);
}

// LES HOOKS COMPOSITES ci-dessous appellent le hook de CHAQUE module du
// registre, dans l'ordre de la liste, à chaque rendu — pas seulement celui
// du type affiché : un hook appelé dans une boucle reste dans l'ordre des
// hooks de React tant que la boucle fait toujours les mêmes tours, et la
// liste est fixée au démarrage de l'application (le registre ne change
// plus). Le type de la page peut changer d'un rendu à l'autre (une
// Application lue) sans changer les hooks appelés. Un seul module est actif,
// le premier du type (`moduleOf`) ; un module inactif ne lit rien : il
// reçoit `project` indéfini, ou `active` et `enabled` (`available`) faux.

/** Aucune version : ni badge, ni entrée d'en-tête. */
const NO_VERSIONS: EnvironmentVersions = {
  badge: () => undefined,
  headerItem: () => undefined,
};

/** Les versions des environnements de `project`, par le module de `appType`. */
export function useEnvironmentVersions(
  appType: string | undefined,
  project: string | undefined,
  refreshKey?: unknown,
): EnvironmentVersions {
  let shown = NO_VERSIONS;
  const modules = useAppTypeModules();
  const chosen = moduleOf(modules, appType);
  for (const module of modules) {
    const active = module === chosen;
    const versions = module.versions?.useVersions({
      project: active ? project : undefined,
      refreshKey,
    });
    if (active && versions) {
      shown = versions;
    }
  }
  return shown;
}

/** Rien à dire de la disponibilité de l'application. */
const NO_READINESS: BranchReadiness = {};

/** La disponibilité de l'application d'une branche, par le module de `appType`. */
export function useBranchReadiness(
  appType: string | undefined,
  context: Omit<ReadinessContext, 'active'>,
): BranchReadiness {
  let shown = NO_READINESS;
  const modules = useAppTypeModules();
  const chosen = moduleOf(modules, appType);
  for (const module of modules) {
    const active = module === chosen;
    const readiness = module.branch?.useReadiness?.({
      ...context,
      active,
      enabled: active && context.enabled,
    });
    if (active && readiness) {
      shown = readiness;
    }
  }
  return shown;
}

/** Aucun outil dans la carte des pods. */
const NO_POD_TOOLS: PodTools = { busy: false };

/** Les outils de la carte des pods d'une branche, par le module de `appType`. */
export function useBranchPodTools(
  appType: string | undefined,
  context: Omit<PodToolsContext, 'active'>,
): PodTools {
  let shown = NO_POD_TOOLS;
  const modules = useAppTypeModules();
  const chosen = moduleOf(modules, appType);
  for (const module of modules) {
    const active = module === chosen;
    const tools = module.branch?.usePodTools?.({
      ...context,
      active,
      available: active && context.available,
    });
    if (active && tools) {
      shown = tools;
    }
  }
  return shown;
}

/**
 * Le module de ce que nomme le descripteur de la vue : celui de `appType`,
 * et pour un type inconnu (indéfini : un projet sans label ni annotation de
 * type, ou pas encore lu) celui de `LEGACY_APP_TYPE` — la règle de
 * `fallbackDescriptor` (shared/useAppTypeDescriptor.ts) et de `appTitle.ts`,
 * qui donnent alors le descripteur et le nom d'Odoo : les noms des pods et
 * le résumé de la promotion suivent le même type que le descripteur. Les
 * autres apports (versions, disponibilité, outils des pods, onglet
 * Configuration) gardent `moduleOf` : aucun module tant que le type n'est
 * pas connu (étape 13 : aucune requête d'un type supposé ; un Skeleton pour
 * l'onglet).
 */
function namingModuleOf(
  modules: readonly AppTypeModule[],
  appType: string | undefined,
): AppTypeModule | undefined {
  return moduleOf(modules, appType ?? LEGACY_APP_TYPE);
}

/**
 * La liste « Ce qui ne change pas » du module de `appType`
 * (`namingModuleOf`), s'il en apporte une.
 */
export function useKeptSummary(
  appType: string | undefined,
): ComponentType<KeptSummaryProps> | undefined {
  return namingModuleOf(useAppTypeModules(), appType)?.promotion?.KeptSummary;
}

/** Aucun rôle de pod propre au type. */
const NO_POD_ROLES: readonly PodRoleRule[] = Object.freeze([]);

/**
 * Les rôles des pods propres au chart de `appType`, par son module
 * (`namingModuleOf`).
 */
export function usePodRoleRules(
  appType: string | undefined,
): readonly PodRoleRule[] {
  let shown = NO_POD_ROLES;
  const modules = useAppTypeModules();
  const chosen = namingModuleOf(modules, appType);
  for (const module of modules) {
    const rules = module.pods?.useRoles();
    if (module === chosen && rules) {
      shown = rules;
    }
  }
  return shown;
}
